import { UserBaseDto, UserDto } from './user.dto';
import { IsDate, IsEnum, IsInt, IsNotEmpty, IsNumber, IsOptional, IsString, Max, MaxLength, Min } from 'class-validator';
import { Type } from 'class-transformer';
import { PayOrderStatus } from './enums/payorder';
import { PayOrderDto } from './payorder.dto';
import { WORK_SHIFT_MANUAL_CLOSE_REASON_MAX_LENGTH, WORK_SHIFT_PLAN_MAX, WORK_SHIFT_PLAN_MIN } from './enums/work-shift';

export class TimeSheetDataRowDto {
  user: UserDto | null;
  closedWorkingHours: number | null;
  closedWorkShiftsCount: number;
  payload: string;
}

export class TimeSheetDataDto {
  dates: number[];
  rows: TimeSheetDataRowDto[];
}

export class UserTimeSheetDataDto {
  dates: number[];
  row: TimeSheetDataRowDto;
}

export enum SALARY_ENTRY_TYPES {
  SEEK_COMPENSATION = 'seek_compensation',
  VACATION_COMPENSATION = 'vacation_compensation',
  BONUS = 'bonus',
  COMMISSION = 'commission',
  OTHER_ADDITIONS = 'other_additions',
  BASE_SALARY = 'base_salary',
  PREMIUM_FUND_BONUS = 'premium_fund_bonus',
  PERCENTAGE_SUPERVISOR = 'prc_supervisor',

  PREPAYMENT = 'prepayment',
  PENALTY = 'penalty',
  TAXES = 'taxes',
  OTHER_DEDUCTIONS = 'other_deductions',
  PREMIUM_FUND_RETENTION = 'premium_fund_retention',
}

export const salaryTypeLabels: Record<SALARY_ENTRY_TYPES, string> = {
  [SALARY_ENTRY_TYPES.BONUS] : 'Премия',
  [SALARY_ENTRY_TYPES.SEEK_COMPENSATION] : 'Больничные',
  [SALARY_ENTRY_TYPES.VACATION_COMPENSATION] : 'Отпускные',
  [SALARY_ENTRY_TYPES.COMMISSION ]:'Проценты',
  [SALARY_ENTRY_TYPES.OTHER_ADDITIONS] : 'Прочие начисления',
  [SALARY_ENTRY_TYPES.BASE_SALARY] : 'Оклад',
  [SALARY_ENTRY_TYPES.PREMIUM_FUND_BONUS]: 'Премия из фонда',
  [SALARY_ENTRY_TYPES.PERCENTAGE_SUPERVISOR]: 'Процент руководителя',

  [SALARY_ENTRY_TYPES.PENALTY] : 'Штраф',
  [SALARY_ENTRY_TYPES.OTHER_DEDUCTIONS] : 'Прочие удержания',
  [SALARY_ENTRY_TYPES.PREPAYMENT] : 'Аванс',
  [SALARY_ENTRY_TYPES.TAXES] : 'Налоги',
  [SALARY_ENTRY_TYPES.PREMIUM_FUND_RETENTION]: 'Удержание в фонд'
};

export const ADDITION_TYPES = [
  SALARY_ENTRY_TYPES.BONUS,
  SALARY_ENTRY_TYPES.SEEK_COMPENSATION,
  SALARY_ENTRY_TYPES.VACATION_COMPENSATION,
  SALARY_ENTRY_TYPES.COMMISSION,
  SALARY_ENTRY_TYPES.OTHER_ADDITIONS,
  SALARY_ENTRY_TYPES.BASE_SALARY,
  SALARY_ENTRY_TYPES.PREMIUM_FUND_BONUS,
  SALARY_ENTRY_TYPES.PERCENTAGE_SUPERVISOR
];
export const DEDUCTION_TYPES = [
  SALARY_ENTRY_TYPES.PENALTY,
  SALARY_ENTRY_TYPES.OTHER_DEDUCTIONS,
  SALARY_ENTRY_TYPES.PREPAYMENT,
  SALARY_ENTRY_TYPES.TAXES,
  SALARY_ENTRY_TYPES.PREMIUM_FUND_RETENTION
];


export type SalarySummary = {
  [Key in SALARY_ENTRY_TYPES]: number;
};

export class CreateSalaryEntryDto {
  @IsNumber()
  @Min(0.01)
    sum: number;

  @IsString()
  @IsEnum(SALARY_ENTRY_TYPES)
  @IsNotEmpty()
    type: SALARY_ENTRY_TYPES;

  @IsString()
  @MaxLength(255)
    description: string;
}

export class SalaryEntryDto extends CreateSalaryEntryDto {
  id: number;
  year: number;
  month: number;
  user: UserDto;
  createdBy: UserDto;
  invoiceId?: number;

  salaryEntryId?: number;

  @IsDate()
  @Type(() => Date)
    createdAt: Date;
}

export class SalaryCommissionRowDto {
  id: number;
  createdBy: UserBaseDto;

  @IsDate()
  @Type(() => Date)
    createdAt: Date;

  sum: number;
  invoiceId: number;
  invoiceNumber: string;
  invoiceTotal: number;
  invoiceProfit: number;
  managerPercent: number;
}

export class CreateSalaryEntryPremiumFundRetentionDto {
  year: number;
  month: number;
  user: UserBaseDto;
}

export class CreateSalaryPercentageSupervisorDto {
  year: number;
  month: number;
  user: UserBaseDto;
}

export class CreateSalaryEntryPremiumFundIssuanceDto {
  year: number;
  month: number;
  premiumFund: number[];
  user: UserBaseDto;
}

export declare class SalaryEntryBaseDto {
  id: number;
  year: number;
  month: number;
  user: UserBaseDto;
  createdBy: UserBaseDto;
  invoiceId?: number;
  salaryEntryId?: number;
  sum: number;
}

export class SalaryFundDto {
  user: UserBaseDto;
  countInvoices: number;
  countInvoicesClosed: number;
  managerTotalProfit: number;
  premiumFundSum: number;
  premiumFundSumMore: number;
  premiumFundTotalSum: number;
  salaryEntriesInFund: SalaryEntryBaseDto[];
  baseSalary: boolean;
  payOrderRow: boolean;
}

export class SalarySummaryDto implements SalarySummary {
  prc_supervisor: number;
  premium_fund_bonus: number;
  premium_fund_retention: number;
  base_salary: number;
  bonus: number;
  commission: number;
  other_additions: number;
  other_deductions: number;
  penalty: number;
  prepayment: number;
  seek_compensation: number;
  taxes: number;
  vacation_compensation: number;
  total: number;

  year: number;
  month: number;
  userId: number;
  firstName: string;
  secondName: string;
}

export class CreatePrepaymentDto {
  year: number;
  month: number;
  sum: number;
  userId: number;
  status: PayOrderStatus;
  description: string;
  accountId: number;
  paymentDate: Date;
}

export class PrepaymentDto extends CreatePrepaymentDto {
  id: number;
}

export class UpdatePrepaymentDto extends PrepaymentDto {
}

export class SalaryPayOrderDto {
  salaryEntry: SalaryEntryDto;
  payOrder: PayOrderDto | null;
  total: number;
  isClosed: boolean;
  closedAt: Date | null;
}
export class SalarySummaryPayOrderDto {
  total: number;
  year: number;
  month: number;
  userId: number;
}

export class WorkShiftTaskInvoiceDto {
  id: number;
  invoiceNumber: string;
  status: number;
  managerId: number;
  manager: UserBaseDto | null;
}

export class WorkShiftInvoiceTasksDto {
  items: WorkShiftTaskInvoiceDto[];
  total: number;
}

export class WorkShiftProgressDto {
  user: UserBaseDto;
  callsPlan: number;
  offersPlan: number;
  callsFact: number;
  offersFact: number;
  hasOpenShift: boolean;
}

export class WorkShiftDto {
  id: number;
  date: string;
  user: UserDto;
  openedAt: Date;
  closedAt: Date | null;
  closedBy: UserDto | null;
  callsPlan: number;
  offersPlan: number;
  callsFact: number;
  offersFact: number;
  manualCloseReason: string | null;
}

export class WorkShiftPlanDto {
  date: string;
  shouldOpenShift: boolean;
  shift: WorkShiftDto | null;
  callsPlan: number;
  offersPlan: number;
  callsFact: number;
  offersFact: number;
  waitingLeadsCount: number;
  debtInvoices: WorkShiftInvoiceTasksDto;
  paidInvoicesWithoutWcc: WorkShiftInvoiceTasksDto;
  showManagers: boolean;
  subordinateProgress: WorkShiftProgressDto[];
}

export class UpdateWorkShiftDto {
  @Type(() => Number)
  @IsInt({ message: 'План звонков должен быть целым числом' })
  @Min(WORK_SHIFT_PLAN_MIN, { message: `План звонков должен быть не меньше ${WORK_SHIFT_PLAN_MIN}` })
  @Max(WORK_SHIFT_PLAN_MAX, { message: `План звонков должен быть не больше ${WORK_SHIFT_PLAN_MAX}` })
    callsPlan: number;

  @Type(() => Number)
  @IsInt({ message: 'План КП должен быть целым числом' })
  @Min(WORK_SHIFT_PLAN_MIN, { message: `План КП должен быть не меньше ${WORK_SHIFT_PLAN_MIN}` })
  @Max(WORK_SHIFT_PLAN_MAX, { message: `План КП должен быть не больше ${WORK_SHIFT_PLAN_MAX}` })
    offersPlan: number;

  @IsOptional()
  @IsString({ message: 'Причина ручного закрытия должна быть строкой' })
  @MaxLength(WORK_SHIFT_MANUAL_CLOSE_REASON_MAX_LENGTH)
    manualCloseReason?: string | null;
}
